import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PrimaryCarePhysician, Specialists } from 'src/app/beans/health-history/general-info.bean';
import { MASK_CONSTANTS } from 'src/app/constants/mask.constants';
import { SpinnerComponent } from 'src/app/modules/shared/spinner/spinner.component';
import { ValidationUtilityService } from 'src/app/modules/shared/utils/validation.util/validation-utility.service';
import { GeneralInfoService } from 'src/app/services/ha-new/general-info.service';
import { NewHAService } from 'src/app/services/ha-new/new-ha.service';

/**
 * component containing primary care and specialists section of HA
 */
@Component({
	selector: 'app-primary-care',
	templateUrl: './primary-care.component.html',
	styleUrls: ['./primary-care.component.scss'],
})
export class PrimaryCareComponent implements OnInit {
	/** holds primary care physician form */
	primaryCareForm: FormGroup;
	/** holds specialists form */
	specialistsForm: FormGroup<{
		seeMedicalSpecialist: FormControl<boolean>;
		specialists: FormArray<
			FormGroup<{
				fullName: FormControl<any>;
				specialty: FormControl<any>;
				phone: FormControl<any>;
			}>
		>;
	}>;
	/** masks phone number in format (123) 456-7890 */
	phoneMask = MASK_CONSTANTS.CELL_PHONE;
	/** holds ha id */
	healthAssessmentID: number;
	/** Data carrier subscriptions */
	subscriptions: Subscription[] = [];
	/** holds appointment id */
	apptID: any;
	/** holds readOnly flag */
	readOnly: boolean = false;
	scrollTo = '';
	returningUser: boolean = false;

	/**
	 * constructor
	 * @param generalInfoService
	 * @param spinner
	 */
	constructor(
		private generalInfoService: GeneralInfoService,
		private spinner: SpinnerComponent,
		private activatedRoute: ActivatedRoute,
		private haService: NewHAService,
		private router: Router
	) {
		this.activatedRoute.queryParams.subscribe(params => {
			this.apptID = params.apptID;
			this.readOnly = params.readOnly === 'true';
			this.healthAssessmentID = params.healthAssessmentID;
			this.returningUser = params.returningUser === 'true';
			if (this.scrollTo != 'false') this.scrollTo = params.section;
		});
	}

	/**
	 * on component intialization
	 */
	ngOnInit() {
		this.initializePrimaryCareForm();
		this.initializeSpecialistForm();
		// If there is no appt id and health assessment id - service returns 400 and so not making calls
		if (this.apptID && this.healthAssessmentID) {
			this.presetFormFields();
		}
	}

	ngAfterViewChecked() {
		if (this.scrollTo != 'false') {
			if (this.haService.scrollView(this.scrollTo)) {
				this.scrollTo = 'false';
			}
		}
	}

	/**
	 * initializes primary care physician form
	 * @param primaryCarePhysician
	 */
	initializePrimaryCareForm(primaryCarePhysician: PrimaryCarePhysician = new PrimaryCarePhysician()) {
		this.primaryCareForm = new FormGroup({
			havePrimaryCarePhysician: new FormControl({
				value: '',
				disabled: this.readOnly,
			}),
			fullName: new FormControl(''),
			phone: new FormControl('', Validators.compose([ValidationUtilityService.haOptionalPhoneNumberValidator])),
			fax: new FormControl('', Validators.compose([ValidationUtilityService.haOptionalPhoneNumberValidator])),
		});
	}

	/**
	 * initializes speciality form
	 */
	initializeSpecialistForm(specialistInfo: Specialists = new Specialists()) {
		this.specialistsForm = new FormGroup({
			seeMedicalSpecialist: new FormControl({
				value: false,
				disabled: this.readOnly,
			}),
			specialists: new FormArray([]),
		});
	}

	/**
	 * adds specialist to form array
	 * @param specialist
	 */
	addSpecialist(specialist: any = '') {
		const control = this.specialistsForm.controls.specialists;
		control.push(
			new FormGroup({
				fullName: new FormControl(specialist.fullName || ''),
				specialty: new FormControl(specialist.specialty || ''),
				phone: new FormControl(
					specialist.phone || '',
					Validators.compose([ValidationUtilityService.haOptionalPhoneNumberValidator])
				),
			})
		);
	}

	/**
	 * presets form with data from service
	 */
	presetFormFields() {
		this.getPrimaryCareInfo();
		this.getSpecialistsInfo();
	}

	/**
	 * gets primary care physician info
	 */
	getPrimaryCareInfo() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.generalInfoService.getPrimaryCarePhysicianInfo(this.apptID, this.healthAssessmentID).subscribe(
				response => {
					this.spinner.stopLoader();
					this.primaryCareForm.controls.havePrimaryCarePhysician.setValue(response.data.havePrimaryCarePhysician);
					this.primaryCareForm.controls.fullName.setValue(response.data.fullName);
					this.primaryCareForm.controls.phone.setValue(response.data.phone);
					this.primaryCareForm.controls.fax.setValue(response.data.fax);
				},
				err => {
					this.spinner.stopLoader();
					console.log(err);
				}
			)
		);
	}

	/**
	 * presets specialists info with data from service
	 */
	getSpecialistsInfo() {
		this.spinner.startLoader();
		this.subscriptions.push(
			this.generalInfoService.getSpecialistsInfo(this.apptID, this.healthAssessmentID).subscribe(
				response => {
					this.spinner.stopLoader();
					this.specialistsForm.controls.seeMedicalSpecialist.setValue(response.data.seeMedicalSpecialist);
					if (response.data.specialists) {
						response.data.specialists.forEach(specialist => {
							this.addSpecialist(specialist);
						});
					}
				},
				err => {
					this.spinner.stopLoader();
					console.log(err);
				}
			)
		);
	}

	/**
	 * saves data on clicking next
	 */
	next() {
		if (!this.readOnly) {
			if (this.primaryCareForm.valid && this.specialistsForm.valid) {
				this.savePrimaryCarePhysicianInfo();
				this.removeEmptyEntriesFromSpecialistArray();
				this.saveSpecialistInfo();
				this.haService.redirect(
					[],
					['medical-condition'],
					[],
					this.apptID,
					this.healthAssessmentID,
					this.readOnly,
					'medical-medical-condition',
					this.returningUser
				);
			}
		}
	}

	/**
	 * removes empty entry from array before saving
	 */
	removeEmptyEntriesFromSpecialistArray() {
		const control = this.specialistsForm.controls.specialists;
		this.specialistsForm.getRawValue().specialists.forEach((specialist, index) => {
			if (!specialist.fullName && !specialist.specialty && !specialist.phone) {
				control.removeAt(index);
			}
		});
	}

	/**
	 * saves primary care physician info
	 */
	savePrimaryCarePhysicianInfo() {
		this.subscriptions.push(
			this.generalInfoService
				.savePrimaryCarePhysicianInfo(this.apptID, {
					...this.primaryCareForm.getRawValue(),
					healthAssessmentID: this.healthAssessmentID,
				})
				.subscribe(
					res => {
						console.log(res);
					},
					err => {
						console.log(err);
					}
				)
		);
	}

	/**
	 * saves specialist info
	 */
	saveSpecialistInfo() {
		this.subscriptions.push(
			this.generalInfoService
				.saveSpecialistsInfo(this.apptID, {
					...this.specialistsForm.getRawValue(),
					healthAssessmentID: this.healthAssessmentID,
				})
				.subscribe(
					res => {
						console.log(res);
					},
					err => {
						console.log(err);
					}
				)
		);
	}

	/**
	 * Destroys the component
	 */
	ngOnDestroy() {
		this.subscriptions.forEach(subscription => subscription.unsubscribe());
		this.subscriptions = [];
	}
}
